<template>
  <div id="lead-form-tab">
    <vx-card class="py-3" :noShadow="true">

    <div class="md:grid grid-cols-3 gap-4">
      <!-- user form -->
      <div class="col-span-2">
        <div class="form-parent">

          <div class="title-and-desc">
            <h4>{{ $t('create_campaign.lead_form.creation.title') }}</h4>
            <p v-html="$t('create_campaign.lead_form.creation.description')"></p>
          </div>

          <div v-if="editing && !isDuplicate">
            <vs-input
              :label="$t('create_campaign.lead_form.creation.form_name_label')"
              :value="value.form.name"
              class="w-full cursor-not-allowed"
              :disabled="true"
            >
            </vs-input>
          </div>
          <div v-else>
            <div class="form-name-parent">
              <vs-input
                v-model="value.form.name"
                maxlength="30"
                :label="$t('create_campaign.lead_form.creation.form_name_label')"
                :placeholder="$t('Form name')"
                :data-vv-as="'form name'"
                :name="'step6.form.name'"
                class="w-full"
                :danger="$vErrors.has([errors, serverErrors], 'step6.form.name')"
              >
              </vs-input>
              <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.name')" class="text-danger">
                {{ $vErrors.first([errors, serverErrors], 'step6.form.name') }}
              </span>
            </div>

            <div class="form-language">
              <h4 class="mb-4">{{ $t('create_campaign.lead_form.creation.form_language_label') }}</h4>
              <vs-radio v-model="value.form.lang_iso2_code" vs-name="language" vs-value="en">{{ $t("create_campaign.lead_form.creation.lang_en") }}</vs-radio>
              <vs-radio v-model="value.form.lang_iso2_code" vs-name="language" vs-value="ar">{{ $t("create_campaign.lead_form.creation.lang_ar") }}</vs-radio>

              <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.name')" class="text-danger">
                {{ errors.first('step6.form.language') }}
              </span>
            </div>

            <div class="first-divider"></div>

            <vs-collapse ref="createAudienceCollapse" class="p-0 bg-white" accordion>
            <div class="intro-section">
              <vs-collapse-item :open="false" @click.native="() => moveToPage(PAGES.FIRST)">
              <div class="font-normal text-2xl" slot="header">
                <h4 :class="[hasErrorOnSectionX('intro') ? 'text-danger' : '']">{{ $t('create_campaign.lead_form.creation.intro_title') }}</h4>
                <p class="intro-desc">{{ $t("create_campaign.lead_form.creation.intro_hint") }}</p>
              </div>
              <div class="intro-content">
                <vs-input
                  v-model="value.form.intro.title"
                  maxlength="30"
                  :data-vv-as="'headline'"
                  :placeholder="$t('create_campaign.lead_form.creation.intro_title_label')"
                  class="w-full"
                  :name="'step6.form.intro.title'"
                  :label="$t('Headline')"
                  :danger="$vErrors.has([errors, serverErrors], 'step6.form.intro.title')"
                >
                </vs-input>

              <div class="vs-row">
                <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.intro.title')" class="text-danger float-left">
                  {{ $vErrors.first([errors, serverErrors], 'step6.form.intro.title') }}
                </span>
                <div class="letter-counter float-right">{{ value.form.intro.title.length + '/30' }}</div>
              </div>

              <div class="clear-both"></div>

              <label class="text-area-label">{{$t('create_campaign.lead_form.creation.intro_desc_label')}}</label>
              <textarea
                v-model="value.form.intro.description"
                maxlength="200"
                :placeholder="$t('create_campaign.lead_form.creation.intro_desc_placeholder')"
                :data-vv-as="'description'"
                class="w-full desc-textarea"
                v-bind:class="{ 'text-area-has-error': $vErrors.has([errors, serverErrors], 'step6.form.intro.description') }"
                :name="'step6.form.intro.description'"
                :danger="$vErrors.has([errors, serverErrors], 'step6.form.intro.description')"
              >
              </textarea>

              <div class="vs-row">
                <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.intro.description')" class="text-danger float-left">
                  {{ $vErrors.first([errors, serverErrors], 'step6.form.intro.description') }}
                </span>
                <div class="desc-letter-counter letter-counter float-right">{{ value.form.intro.description.length + '/200' }}</div>
              </div>
            </div>
            </vs-collapse-item>
          </div>

          <div class="divider"></div>

          <div class="questions-section">
            <vs-collapse-item @click.native="() => moveToPage(PAGES.FIRST)">
            <div class="font-normal text-2xl" slot="header">
              <h4 :class="[hasErrorOnSectionX('properties.email') || hasCustomQuestionsError() ? 'text-danger' : '']">{{ $t('create_campaign.lead_form.creation.questions_label') }}</h4>
              <p v-html="$t('create_campaign.lead_form.creation.questions_hint')"></p>
            </div>
            <div class="questions-content space-y-5">
              <span>
                <h4 class="select-information-title">{{ $t('Select the information that you need') }}</h4>
                <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.properties.email')" class="text-danger">
                  {{ $vErrors.first([errors, serverErrors], 'step6.form.properties.email') }}
                </span>
              </span>
              <div class="options">
                <vs-checkbox v-model="formPropertiesNames" :vs-value="FULL_NAME_KEY" class="ml-0">{{ $t('create_campaign.lead_form.creation.full_name') }}</vs-checkbox>
                <vs-checkbox v-model="formPropertiesNames" :vs-value="EMAIL_KEY" class="ml-0">{{ $t('create_campaign.lead_form.creation.email') }}</vs-checkbox>
                <vs-checkbox :checked="isNumberOptionChecked" @change="changeNumberOptionStatus($event)" class="ml-0">{{ $t('create_campaign.lead_form.creation.number') }}</vs-checkbox>

                <div class="phone-options">
                  <vs-radio style="color:red" :disabled="disableNumbers" v-model="numberOption" @change="updatePropsWithNumberOption($event, WHATSAPP_NUMBER_KEY)" :vs-value="PHONE_NUMBER_KEY" class="phone-number-input" vs-name="phone-options" :class="{'disabled cursor-not-allowed': disableNumbers}">{{ $t('create_campaign.lead_form.creation.phone_number') }}</vs-radio> <br>
                  <vs-radio :disabled="disableNumbers" v-model="numberOption" @change="updatePropsWithNumberOption($event, PHONE_NUMBER_KEY)" :vs-value="WHATSAPP_NUMBER_KEY" vs-name="phone-options" :class="{'disabled cursor-not-allowed': disableNumbers}">{{ $t('create_campaign.lead_form.creation.whatsapp_number') }}</vs-radio>
                </div>
              </div>
            </div>

            <div class="custom-questions mt-6" v-if="$hasFeature('lead_form_custom_questions')">
              <custom-questions-form
                @custom-questions-updated="updateCustomQuestions"
                @questions-change="$refs['lead-form-preview'].scrollToBottom()"
                v-model="value"
                :serverErrors="serverErrors"
              >
              </custom-questions-form>
            </div>
            </vs-collapse-item>
          </div>

          <div class="divider"></div>
            <div class="thank-you-screen">
              <vs-collapse-item @click.native="() => moveToPage(PAGES.SECOND)">
              <div class="font-normal text-2xl" slot="header">
                <h4 :class="[hasErrorOnSectionX('thank_you') ? 'text-danger' : '']">{{ $t('create_campaign.lead_form.creation.thank_you_title') }}</h4>
                <p v-html="$t('create_campaign.lead_form.creation.thank_you_hint')"></p>
              </div>

              <div class="thank-you-screen-content">
                <vs-input
                  v-model="value.form.thank_you.title"
                  maxlength="30"
                  :data-vv-as="$t('headline')"
                  :placeholder="$t('Headline')"
                  class="w-full"
                  :label="$t('Headline')"
                  :danger="$vErrors.has([errors, serverErrors], 'step6.form.thank_you.title')"
                  :name="'step6.form.thank_you.title'"
                >
                </vs-input>

                <div class="vs-row">
                  <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.thank_you.title')" class="text-danger float-left">
                    {{ $vErrors.first([errors, serverErrors], 'step6.form.thank_you.title') }}
                  </span>
                  <div class="letter-counter float-right">{{ value.form.thank_you.title.length + '/30' }}</div>
                </div>

                <div class="clear-both"></div>

                <label class="text-area-label">{{$t('create_campaign.lead_form.creation.thank_you_desc_label')}}</label>
                <textarea
                  v-model="value.form.thank_you.description"
                  maxlength="200"
                  :data-vv-as="$t('description')"
                  :placeholder="$t('create_campaign.lead_form.creation.thank_you_desc_placeholder')"
                  class="w-full textarea thank-you-textarea"
                  v-bind:class="{ 'text-area-has-error': $vErrors.has([errors, serverErrors], 'step6.form.thank_you.description') }"
                  label=""
                  :danger="$vErrors.has([errors, serverErrors], 'step6.form.thank_you.description')"
                  :name="'step6.form.thank_you.description'"
                >
                </textarea>
                <span class="vs-row">
                  <span v-if="$vErrors.has([errors, serverErrors], 'step6.form.thank_you.description')" class="text-danger float-left">
                    {{ $vErrors.first([errors, serverErrors], 'step6.form.thank_you.description') }}
                  </span>
                  <div class="letter-counter float-right">{{ value.form.thank_you.description.length + '/200' }}</div>
                </span>
              </div>
              </vs-collapse-item>
            </div>
            </vs-collapse>
          </div>
        </div>
      </div>
      <!-- user form -->

      <!-- live preivew -->
      <lead-form-mobile-preview
        :editing="editing"
        v-model="value"
        :screenNum="screenNum"
        ref="lead-form-preview"
      >
      </lead-form-mobile-preview>
      <!-- live preivew -->
    </div>

    </vx-card>
  </div>
</template>

<script>
import FormSchema from '@/utils/LeadFormSchema'
import CustomQuestionsForm from './components/AdsetAds/components/LeadFormMobilePreview/custom-form.vue'
import LeadFormMobilePreview from './components/AdsetAds/components/LeadFormMobilePreview/main-preview.vue'

export default {
  props: {
    value: {
      required: true,
      type: Object
    },
    serverErrors: {
      required: true
    },
    editing: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  components: {
    CustomQuestionsForm,
    LeadFormMobilePreview
  },

  data () {
    return {
      FULL_NAME_KEY: 'full_name',
      EMAIL_KEY: 'email',
      NUMBER_KEY: 'number',
      PHONE_NUMBER_KEY: 'phone_number',
      WHATSAPP_NUMBER_KEY: 'whatsapp_number',
      DEFAULT_QUESTIONS: ['full_name', 'email'],
      LANGS: {
        EN: 'en',
        AR: 'ar'
      },
      PAGES: {
        FIRST: 1,
        SECOND: 2
      },
      screenNum : 1,
      numberOption: null,
      formPropertiesNames: [],
      previewHeadline: 'Headline',
      previewDescription: 'Description'
    }
  },

  async created () {
    this.setFormPropsNames()
  },

  computed: {
    isNumberOptionChecked () {
      return this.numberOption && [this.PHONE_NUMBER_KEY, this.WHATSAPP_NUMBER_KEY].includes(this.numberOption)
    },

    isDuplicate () {
      return !!this.$route.params.duplicate || false
    },

    disableNumbers () {
      return !this.isNumberOptionChecked
    }
  },

  watch: {
    'value': {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },

    formPropertiesNames () {
      this.updateProperties()
    }
  },

  methods: {
    changeNumberOptionStatus (event) {
      let numberOption = null

      if (event.target.checked) {
        this.formPropertiesNames.push(this.PHONE_NUMBER_KEY)

        numberOption = this.PHONE_NUMBER_KEY
      } else {
        this.formPropertiesNames = this.formPropertiesNames.filter(
          (el) => el !== this.PHONE_NUMBER_KEY && el !== this.WHATSAPP_NUMBER_KEY
        )
      }

      this.numberOption = numberOption

      this.updateProperties()
    },

    updatePropsWithNumberOption (event, oldNumberKey) {
      this.formPropertiesNames = this.formPropertiesNames.filter((el) => el !== oldNumberKey)

      this.formPropertiesNames.push(event.target.value)
    },

    setFormPropsNames () {
      if (this.isDuplicate || this.editing) {
        this.formPropertiesNames = Object.keys(this.value.form.properties.primitive)

        if (this.formPropertiesNames.includes(this.PHONE_NUMBER_KEY)) {
          this.numberOption = this.PHONE_NUMBER_KEY
        } else if (this.formPropertiesNames.includes(this.WHATSAPP_NUMBER_KEY)) {
          this.numberOption = this.WHATSAPP_NUMBER_KEY
        }
      } else {
        this.formPropertiesNames = this.DEFAULT_QUESTIONS
      }

      this.updateProperties()
    },

    moveToPage (pageNumber) {
      this.screenNum = pageNumber
    },

    hasErrorOnSectionX (section) {
      return this.hasError(`step6.form.${section}.title`) ||
        this.hasError(`step6.form.${section}.description`) ||
        this.hasError(`step6.form.${section}`)
    },

    hasError (section) {
      return this.$vErrors.has([this.errors, this.serverErrors], section)
    },


    hasCustomQuestionsError () {
      return /step6.form.properties/g.test(JSON.stringify(this.errors))
    },

    updateProperties () {
      this.value.form.properties.primitive = {}

      for (const name of this.formPropertiesNames) {
        this.value.form.properties.primitive[name] = FormSchema.properties[name]
      }
    },

    updateCustomQuestions (questions) {
      this.value.form.properties.custom = questions
    }
  }
}
</script>

<style lang="scss" scoped>

.form-parent {
  /deep/ .vs-input--label {
    padding-left: 0px;
    font-size: 14px;
  }

  /deep/ .vs-con-input {
    margin-top: 6px !important;
    margin-left: 0 !important;
  }
  .select-information-title {
    margin-bottom: 24px;
  }
  /deep/ .vs-input--placeholder {
    color: #C2C2C2 !important;
    font-weight: 300;
    text-indent: 5px !important;
  }
  .cursor-not-allowed {
    color: #C2C2C2 !important;
    font-weight: 300;
  }
  .desc-textarea,
  .thank-you-textarea {
    margin-top: 6px;
  }
  .intro-desc,
  .questions-desc,
  .thank-you-screen-desc {
    margin-top: 6px;
    font-weight: 300;
  }

  .title-and-desc {
    h4 {
      color: #2c2c2c;
      font-size: 24px;
      font-weight: 500;
      margin-bottom: 10px;
    }

    p {
      color: #999;
      font-size: 16px;
      font-weight: 300;
      margin-bottom: 24px;
    }
  }

  .form-name-parent {
    margin-bottom: 24px;

    label {
      color: #323232;
      font-size: 14px;
    }
  }

  .form-language {
    h4 {
      color: #000;
      font-weight: 500;
      font-size: 16px;
    }

    /deep/ .vs-radio--label {
      margin-right: 48px !important;
      margin-left: 8px !important;
      font-size: 14px;
    }
  }

  .first-divider {
    width: 100%;
    height: 1px;
    margin-top: 32px;
    margin-bottom: 18px;
    background-color: rgba(189, 195, 202, 0.215527);
  }

  .intro-section,
  .questions-section,
  .thank-you-screen {
    color: #323232;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 6px;

    p {
      color: #999;
      font-size: 14px;
      font-style: normal;
      margin-top: 6px;
      font-weight: 300;
    }
  }

  /deep/ .vs-collapse-item--header,
  /deep/ .con-content--item {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .intro-content,
  .thank-you-screen-content {
    label {
      color: #323232;
      font-weight: normal;
      font-size: 14px;
    }

    .letter-counter {
      color: #c2c2c2;
      font-size: 14px;
      margin-top: 4px;
      // margin-bottom: 16px;
      text-align: right;
      font-weight: normal;
    }

    .desc-letter-counter {
      margin-top: 0px
    }

    textarea {
      width: 100%;
      height: 100px;
      min-width: 100%;
      max-width: 100%;
      min-height: 100px;
      max-height: 100px;
      border-radius: 4px;
      border: 1px solid #c2c2c2;
      font-family: "Poppins", sans-serif;
      padding-top: 6px;
      padding-left: 16px;
      padding-right: 16px;
      font-size: 14px;
    }

    textarea::placeholder {
      color: #c2c2c2;
      padding-left: 0;
      font-weight: 300;
    }

    .text-area-has-error {
      border: 1px solid red;
    }
  }

  .divider {
    background: rgba(189, 195, 202, 0.215527);
    width: 100%;
    height: 1px;
    margin-bottom: 18px;
    margin-top: 18px;
  }

  .questions-content {
    h4 {
      color: #323232;
      font-size: 16px;
      font-weight: 500;
      // margin-bottom: 24px;
    }

    /deep/ .checkbox_x {
      margin-right: 8px;
    }

    /deep/ .vs-checkbox-default {
      color: #323232;
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 24px;
    }

    .phone-options {
      margin-left: 25px;

      /deep/ .vs-radio--label {
        color: #323232;
        font-size: 14px;
        font-weight: 400;
      }

      .phone-number-input {
        margin-bottom: 18px;
      }
    }
  }
}
</style>
